import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { FileText, Mail, MessageSquare, Webhook, Search, Clock, ArrowRight, Sparkles } from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { REPORTS } from "@/constants/testIds";
import { describeCron } from "@/lib/cron";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const CHANNEL_ICON = { email: Mail, slack: MessageSquare, webhook: Webhook };

function rel(ts) {
  if (!ts) return "—";
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return "—";
  }
}

function StatusBadge({ status }) {
  if (!status) return <Badge variant="outline" className="text-[10px] text-muted-foreground">never run</Badge>;
  const map = {
    success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    error: "border-red-500/40 bg-red-500/10 text-red-400",
  };
  return <Badge variant="outline" className={`text-[10px] ${map[status] || "text-muted-foreground"}`}>{status}</Badge>;
}

function ReportRow({ r }) {
  const sched = r.schedules?.[0];
  const channels = [...new Set((r.schedules || []).map((s) => s.delivery))];
  return (
    <Link
      to={`/reports/${r.id}`}
      data-testid={REPORTS.row}
      className="group grid grid-cols-12 items-center gap-3 border-b border-border/40 px-4 py-4 transition-colors last:border-0 hover:bg-muted/30"
    >
      <div className="col-span-12 sm:col-span-5">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FileText className="h-4 w-4 shrink-0 text-accent" />
          <span className="truncate">{r.name}</span>
        </div>
        <p className="mt-1 truncate pl-6 text-xs text-muted-foreground">{r.prompt || "—"}</p>
      </div>

      <div className="col-span-6 sm:col-span-2">
        <div className="flex flex-wrap gap-1">
          {channels.length ? channels.map((c) => {
            const Icon = CHANNEL_ICON[c] || Webhook;
            return (
              <span key={c} className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card/50 px-2 py-0.5 text-[10px] text-muted-foreground">
                <Icon className="h-3 w-3 text-accent" /> {c}
              </span>
            );
          }) : <span className="text-[10px] text-muted-foreground">manual only</span>}
        </div>
      </div>

      <div className="col-span-3 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Last run</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs">
          <StatusBadge status={r.last_run?.status} />
          <span className="text-muted-foreground">{rel(r.last_run?.ran_at)}</span>
        </div>
      </div>

      <div className="col-span-3 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Next</div>
        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {sched ? describeCron(sched.cron).replace(/ ·.*/, "") : "—"}
        </div>
      </div>

      <div className="col-span-12 hidden justify-end sm:col-span-1 sm:flex">
        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
      </div>
    </Link>
  );
}

export default function Reports() {
  const { active } = useWorkspace();
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/reports", { params: { workspace_id: active.id } });
      setReports(data);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return reports;
    return reports.filter((r) => `${r.name} ${r.prompt}`.toLowerCase().includes(t));
  }, [reports, q]);

  return (
    <div data-testid={REPORTS.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-7xl px-6 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Reports</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Saved answers that re-run on a schedule and deliver to your team.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid={REPORTS.search}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reports…"
              className="bg-card/60 pl-9"
            />
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-border/60 bg-card/30">
          {loading ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length ? (
            filtered.map((r) => <ReportRow key={r.id} r={r} />)
          ) : (
            <div data-testid={REPORTS.empty} className="px-4 py-20 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-accent" />
              <h2 className="mt-4 text-lg font-semibold">No reports yet</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Ask a question in <Link to="/chat" className="text-accent hover:underline">Ask</Link>, then choose
                “Save as report”.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
